"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "@/contexts/AuthContext";
import type { OrganisationItemDto } from "@/generated/model";
import { listOrganisationsQueryControllerList } from "@/generated/organisations/organisations";
import { offlineDb, patchNurseryContextRow } from "@/lib/offline/db";

interface Membership {
  id: string;
  organisationId: string;
  role: string;
  status: string;
}

interface OrganisationContextValue {
  // Organisations data
  organisations: OrganisationItemDto[];
  activeOrg: OrganisationItemDto | null;
  activeOrgId: string | null;
  setActiveOrg: (orgId: string) => void;
  // Membership data (moved from AuthContext)
  memberships: Membership[];
  activeMembership: Membership | null;
  // Role detection - Tier 2: Organisation Level (moved from AuthContext)
  isOrgAdmin: boolean;
  isOrgMember: boolean;
  // Loading states
  isLoading: boolean;
  isOffline: boolean;
  refetch: () => Promise<void>;
}

const OrganisationContext = createContext<OrganisationContextValue | null>(
  null,
);

const CACHE_KEY = "organisations";

export function OrganisationProvider({ children }: { children: ReactNode }) {
  const {
    isPlatformAdmin,
    memberships: authMemberships,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth();

  const [organisations, setOrganisations] = useState<OrganisationItemDto[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);

  // Cast memberships from auth to our local type
  const memberships = authMemberships as Membership[];

  // Compute active membership and org roles (moved from AuthContext)
  const { activeMembership, isOrgAdmin, isOrgMember, defaultOrgId } =
    useMemo(() => {
      const active = memberships.find((m) => m.status === "ACTIVE") ?? null;
      return {
        activeMembership: active,
        isOrgAdmin: active?.role === "ADMIN",
        isOrgMember: active?.role === "MEMBER",
        defaultOrgId: active?.organisationId ?? null,
      };
    }, [memberships]);

  // For non-platform-admin users, set the default org from their membership
  useEffect(() => {
    if (!isPlatformAdmin && defaultOrgId && !activeOrgId) {
      setActiveOrgId(defaultOrgId);
    }
  }, [isPlatformAdmin, defaultOrgId, activeOrgId]);

  const activeOrg = useMemo(
    () =>
      activeOrgId
        ? (organisations.find((org) => org.id === activeOrgId) ?? null)
        : null,
    [activeOrgId, organisations],
  );

  // Load cached data from IndexedDB
  const loadFromCache = useCallback(async (): Promise<{
    organisations: OrganisationItemDto[];
    activeOrgId: string | null;
  }> => {
    try {
      const cached = await offlineDb.nurseryContext.get(CACHE_KEY);
      return {
        organisations: (cached?.organisations as OrganisationItemDto[]) ?? [],
        activeOrgId: cached?.activeOrgId ?? null,
      };
    } catch (err) {
      console.error("Failed to load organisations from cache:", err);
    }
    return { organisations: [], activeOrgId: null };
  }, []);

  // Save organisations to IndexedDB cache
  const saveOrganisationsToCache = useCallback(
    async (data: OrganisationItemDto[], orgId?: string | null) => {
      try {
        await patchNurseryContextRow(CACHE_KEY, {
          organisations: data,
          ...(orgId !== undefined ? { activeOrgId: orgId } : {}),
        });
      } catch (err) {
        console.error("Failed to save organisations to cache:", err);
      }
    },
    [],
  );

  // Save active org ID to IndexedDB cache
  const saveActiveOrgToCache = useCallback(async (orgId: string | null) => {
    try {
      await patchNurseryContextRow(CACHE_KEY, { activeOrgId: orgId });
    } catch (err) {
      console.error("Failed to save active org to cache:", err);
    }
  }, []);

  // Fetch organisations from server
  const fetchOrganisations = useCallback(async (): Promise<
    OrganisationItemDto[]
  > => {
    const response = await listOrganisationsQueryControllerList({});
    return (response?.data?.data || []) as OrganisationItemDto[];
  }, []);

  // Set active organisation (with caching)
  const setActiveOrg = useCallback(
    (orgId: string) => {
      setActiveOrgId(orgId);
      saveActiveOrgToCache(orgId);
    },
    [saveActiveOrgToCache],
  );

  // Refetch organisations (can be called manually)
  const refetch = useCallback(async () => {
    if (!navigator.onLine) return;

    try {
      const freshOrgs = await fetchOrganisations();
      setOrganisations(freshOrgs);
      await saveOrganisationsToCache(freshOrgs);
    } catch (err) {
      console.error("Failed to refetch organisations:", err);
    }
  }, [fetchOrganisations, saveOrganisationsToCache]);

  // Initial load effect - only fetch when authenticated
  useEffect(() => {
    // Don't fetch until auth is determined
    if (authLoading) {
      return;
    }

    // If not authenticated, just reset loading state
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    async function initialize() {
      setIsLoading(true);
      setIsOffline(!navigator.onLine);

      // Step 1: Load cached data immediately
      const cached = await loadFromCache();
      if (isMounted) {
        if (cached.organisations.length > 0) {
          setOrganisations(cached.organisations);
        }
        if (cached.activeOrgId) {
          setActiveOrgId(cached.activeOrgId);
        }
      }

      // Step 2: If online, fetch fresh data
      if (navigator.onLine) {
        try {
          const freshOrgs = await fetchOrganisations();
          if (isMounted) {
            setOrganisations(freshOrgs);
            await saveOrganisationsToCache(freshOrgs);
          }
        } catch (err) {
          // If fetch fails, we already have cached data (if any)
          console.error("Online fetch failed, using cached data:", err);
        }
      }

      if (isMounted) {
        setIsLoading(false);
      }
    }

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    authLoading,
    isAuthenticated,
    loadFromCache,
    fetchOrganisations,
    saveOrganisationsToCache,
  ]);

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      // Refetch when coming back online
      refetch();
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refetch]);

  return (
    <OrganisationContext.Provider
      value={{
        organisations,
        activeOrg,
        activeOrgId,
        setActiveOrg,
        memberships,
        activeMembership,
        isOrgAdmin,
        isOrgMember,
        isLoading,
        isOffline,
        refetch,
      }}
    >
      {children}
    </OrganisationContext.Provider>
  );
}

export function useOrganisation() {
  const context = useContext(OrganisationContext);
  if (!context) {
    throw new Error(
      "useOrganisation must be used within an OrganisationProvider",
    );
  }
  return context;
}
